import React, { useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, Settings } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface BreadcrumbConfig {
  label: string;
  translationKey?: string;
}

interface SingularConfig {
  label: string;
  translationKey: string;
}

// Mapping from plural route segments to singular labels for detail pages
const singularLabels: Record<string, SingularConfig> = {
  users: { label: 'User', translationKey: 'common.breadcrumb.singular.user' },
  articles: { label: 'Article', translationKey: 'common.breadcrumb.singular.article' },
  audio: { label: 'Audio', translationKey: 'common.breadcrumb.singular.audio' },
  video: { label: 'Video', translationKey: 'common.breadcrumb.singular.video' },
  programs: { label: 'Program', translationKey: 'common.breadcrumb.singular.program' },
  groups: { label: 'Group', translationKey: 'common.breadcrumb.singular.group' },
  assessments: { label: 'Assessment', translationKey: 'common.breadcrumb.singular.assessment' },
  messages: { label: 'Message', translationKey: 'common.breadcrumb.singular.message' },
  brands: { label: 'Brand', translationKey: 'common.breadcrumb.singular.brand' },
  products: { label: 'Product', translationKey: 'common.breadcrumb.singular.product' },
  subgroups: { label: 'Subgroup', translationKey: 'common.breadcrumb.singular.subgroup' },
  landing: { label: 'Landing Page', translationKey: 'common.breadcrumb.singular.landingPage' },
  triggers: { label: 'Trigger', translationKey: 'common.breadcrumb.singular.trigger' },
  codes: { label: 'Invite Code', translationKey: 'common.breadcrumb.singular.inviteCode' },
  emails: { label: 'Email', translationKey: 'common.breadcrumb.singular.email' },
  roles: { label: 'Role', translationKey: 'common.breadcrumb.singular.role' },
  translations: { label: 'Translation', translationKey: 'common.breadcrumb.singular.translation' },
  modules: { label: 'Module', translationKey: 'common.breadcrumb.singular.module' },
};

export function AdminBreadcrumb() {
  const location = useLocation();
  const { t } = useTranslation('common');

  const routeLabels: Record<string, BreadcrumbConfig> = useMemo(() => ({
    admin: { label: t('common.breadcrumb.admin', 'Admin'), translationKey: "admin.title" },
    articles: { label: t('common.breadcrumb.articles', 'Articles'), translationKey: "admin.articles.title" },
    assessments: { label: t('common.breadcrumb.assessments', 'Assessments'), translationKey: "admin.assessments.title" },
    audio: { label: t('common.breadcrumb.audio', 'Audio'), translationKey: "admin.audio.title" },
    video: { label: t('common.breadcrumb.video', 'Video'), translationKey: "admin.video.title" },
    programs: { label: t('common.breadcrumb.programs', 'Programs'), translationKey: "admin.programs.title" },
    groups: { label: t('common.breadcrumb.groups', 'Groups'), translationKey: "admin.groups.title" },
    messages: { label: t('common.breadcrumb.messages', 'Messages'), translationKey: "admin.messages.title" },
    triggers: { label: t('common.breadcrumb.triggers', 'Triggers'), translationKey: "admin.triggers.title" },
    translations: { label: t('common.breadcrumb.translations', 'Translations'), translationKey: "admin.translations.title" },
    analytics: { label: t('common.breadcrumb.analytics', 'Analytics'), translationKey: "admin.analytics.title" },
    roles: { label: t('common.breadcrumb.user_roles', 'User Roles'), translationKey: "admin.roles.title" },
    emails: { label: t('common.breadcrumb.allowed_emails', 'Allowed Emails'), translationKey: "admin.emails.title" },
    codes: { label: t('common.breadcrumb.invite_codes', 'Invite Codes'), translationKey: "admin.codes.title" },
    "tts-test": { label: t('common.breadcrumb.tts_test', 'TTS Test'), translationKey: "admin.ttsTest.title" },
    "audio-generator": { label: t('common.breadcrumb.audio_generator', 'Audio Generator'), translationKey: "admin.audioGenerator.title" },
    "video-generator": { label: t('common.breadcrumb.video_generator', 'Video Generator'), translationKey: "admin.videoGenerator.title" },
    new: { label: t('common.breadcrumb.new', 'New'), translationKey: "common.new" },
    edit: { label: t('common.breadcrumb.edit', 'Edit'), translationKey: "common.edit" },
    users: { label: t('common.breadcrumb.users', 'Users'), translationKey: "admin.users.title" },
    subgroups: { label: t('common.breadcrumb.subgroups', 'Subgroups'), translationKey: "admin.subgroups.title" },
    enrich: { label: t('common.breadcrumb.enrich', 'Enrich'), translationKey: "admin.enrich.title" },
    generate: { label: t('common.breadcrumb.generate', 'Generate'), translationKey: "admin.generate.title" },
    "chat-test": { label: t('common.breadcrumb.chat_test', 'Chat Test'), translationKey: "admin.chatTest.title" },
    brands: { label: t('common.breadcrumb.brands', 'Brands'), translationKey: "admin.brands.title" },
    products: { label: t('common.breadcrumb.products', 'Products'), translationKey: "admin.products.title" },
    landing: { label: t('common.breadcrumb.landing', 'Landing Pages'), translationKey: "admin.landing.title" },
    modules: { label: t('common.breadcrumb.modules', 'Modules'), translationKey: "admin.modules.title" },
  }), [t]);
  
  const pathSegments = location.pathname
    .split("/")
    .filter((segment) => segment !== "");

  // Don't show breadcrumb on main admin page
  if (pathSegments.length <= 1) {
    return null;
  }

  // Helper to check if a segment is a UUID
  const isUUID = (segment: string) => 
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);

  // Build breadcrumbs with context-aware labels
  const breadcrumbs: { path: string; label: string; isLast: boolean }[] = [];
  let lastEntitySegment: string | null = null;

  for (let i = 0; i < pathSegments.length; i++) {
    const segment = pathSegments[i];
    const isLastSegment = i === pathSegments.length - 1;
    const nextSegment = pathSegments[i + 1];
    
    // Skip UUID segments but track if we need to add a singular label
    if (isUUID(segment)) {
      // If this UUID is the last segment OR next segment is "edit", add singular label
      const needsSingularLabel = isLastSegment || nextSegment === 'edit';
      
      if (needsSingularLabel && lastEntitySegment && singularLabels[lastEntitySegment]) {
        const singularConfig = singularLabels[lastEntitySegment];
        const singularLabel = t(singularConfig.translationKey, singularConfig.label);
        const path = `/${pathSegments.slice(0, i + 1).join("/")}`;
        breadcrumbs.push({
          path,
          label: singularLabel,
          isLast: isLastSegment,
        });
      }
      continue;
    }

    // Skip "edit" segment - we already added the singular label for the entity
    if (segment === 'edit') {
      // If "edit" is followed by a UUID, we'll handle it in the UUID case
      // If "edit" is followed by nothing (last segment), it means we're on an edit page
      // but the UUID was already processed, so just skip
      continue;
    }

    const path = `/${pathSegments.slice(0, i + 1).join("/")}`;
    const config = routeLabels[segment];

    // Track the last entity segment for singular labels
    if (singularLabels[segment]) {
      lastEntitySegment = segment;
    }

    // Get the label
    let label = segment;
    if (config?.translationKey) {
      const translated = t(config.translationKey, config.label);
      label = translated !== config.translationKey ? translated : config.label;
    } else if (config?.label) {
      label = config.label;
    } else {
      // Format unknown segments
      label = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
    }

    breadcrumbs.push({
      path,
      label,
      isLast: isLastSegment,
    });
  }

  // Recalculate isLast after filtering
  if (breadcrumbs.length > 0) {
    breadcrumbs.forEach((crumb, idx) => {
      crumb.isLast = idx === breadcrumbs.length - 1;
    });
  }

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/admin" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
              <Settings className="h-4 w-4" />
              <span>{t('common.breadcrumb.adminPanel', 'Admin Panel')}</span>
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        
        {breadcrumbs.slice(1).map((crumb) => (
          <React.Fragment key={crumb.path}>
            <BreadcrumbSeparator>
              <ChevronRight className="h-4 w-4" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              {crumb.isLast ? (
                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={crumb.path}>{crumb.label}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
